import React, { useState } from 'react'

import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import clsx from 'clsx'

import { formatMonthDayAndYear } from '$extensionSrc/utils/dateUtils'

import styles from './EnterprisePlans.module.scss'
import type { EnterprisePlansQuery } from '../../../__generated__/graphql'

interface EnterprisePlansProps {
  allPlans: EnterprisePlansQuery | undefined
  loading: boolean
}

type Plan = NonNullable<EnterprisePlansQuery['enterprisePlans']>['items'][0]

interface EnterprisePlansRowProps {
  plan: Plan
  isExpanded: boolean
  onToggle: () => void
  rowIndex: number
}

const getRowClassName = (index: number): string =>
  index % 2 === 0
    ? `${styles.dataRow} ${styles.evenRow}`
    : `${styles.dataRow} ${styles.oddRow}`

function EnterprisePlansRow({
  plan,
  isExpanded,
  onToggle,
  rowIndex,
}: EnterprisePlansRowProps) {
  const theme = useTheme()
  return (
    <TableRow className={getRowClassName(rowIndex)}>
      <TableCell className={styles.dataCellName}>
        <Box className={styles.nameContainer}>
          <IconButton
            size="small"
            onClick={onToggle}
            className={styles.expandButton}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Avatar
            className={styles.avatar}
            style={{ backgroundColor: theme.palette.primary.light }}
            onClick={onToggle}
          >
            {plan.orgName.charAt(0)}
          </Avatar>
          <Typography className={styles.orgName} onClick={onToggle}>
            {plan.orgName}
          </Typography>
        </Box>
      </TableCell>
      <TableCell className={styles.dataCell}>
        <Typography className={styles.dataText}>{plan.type}</Typography>
      </TableCell>
      <TableCell className={styles.dataCell}>
        <Typography className={styles.dataText}>
          {formatMonthDayAndYear(plan.planStartDate)}
        </Typography>
      </TableCell>
      <TableCell className={styles.dataCell}>
        <Typography className={styles.dataText}>
          {formatMonthDayAndYear(plan.planExpirationDate)}
        </Typography>
      </TableCell>
      <TableCell className={styles.dataCell}>
        <Typography className={styles.dataText}>
          {plan.maxSeats
            ? `${plan.inUseSeats} / ${plan.maxSeats}`
            : plan.inUseSeats}
        </Typography>
      </TableCell>
      <TableCell className={styles.dataCell}>
        {/* Empty cell for spacing */}
      </TableCell>
    </TableRow>
  )
}

interface EnterprisePlansDetailProps {
  plan: Plan
  isExpanded: boolean
}

function EnterprisePlansDetail({
  plan,
  isExpanded,
}: EnterprisePlansDetailProps) {
  return (
    <TableRow>
      <TableCell colSpan={6} className={styles.expandableCell}>
        <Collapse in={isExpanded} timeout="auto">
          <Box className={styles.expandableContent}>
            {plan.ssoConfigs && (
              <Box className={styles.expandableSection}>
                <Typography
                  variant="subtitle2"
                  className={styles.expandableSectionTitle}
                >
                  SSO Configuration
                </Typography>
                {plan.ssoConfigs.map((config) => (
                  <Box key={config?.slug} sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      className={styles.expandableText}
                    >
                      <strong>Slug:</strong> {config?.slug}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.expandableText}
                    >
                      <strong>Organization URL:</strong> {config?.orgUrl}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.expandableText}
                    >
                      <strong>Domains:</strong>
                    </Typography>
                    <Box className={styles.chipContainer}>
                      {config?.domains?.map((domain) => (
                        <Chip
                          key={domain}
                          label={domain}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {plan.basicSetupDomains && (
              <Box className={styles.expandableSection}>
                <Typography
                  variant="subtitle2"
                  className={styles.expandableSectionTitle}
                >
                  Basic Setup Domains
                </Typography>
                <Box className={styles.chipContainer}>
                  {plan.basicSetupDomains.map((domain) => (
                    <Chip
                      key={domain}
                      label={domain}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            {plan.promoCodes && (
              <Box className={styles.expandableSection}>
                <Typography
                  variant="subtitle2"
                  className={styles.expandableSectionTitle}
                >
                  Promo Codes
                </Typography>
                <Box className={styles.chipContainer}>
                  {plan.promoCodes.map((code) => (
                    <Chip
                      key={code}
                      label={code}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  )
}

export default function EnterprisePlans({
  allPlans,
  loading,
}: EnterprisePlansProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRow = (planId: string) => {
    const newExpandedRows = new Set(expandedRows)
    if (newExpandedRows.has(planId)) {
      newExpandedRows.delete(planId)
    } else {
      newExpandedRows.add(planId)
    }
    setExpandedRows(newExpandedRows)
  }

  if (loading) {
    return (
      <Paper elevation={0} className={clsx(styles.override, styles.container)}>
        <Box className={styles.header}>
          <Skeleton variant="text" className={styles.loadingHeaderTitle} />
          <Skeleton variant="text" className={styles.loadingHeaderCount} />
        </Box>
        <Table className={styles.table}>
          <TableHead>
            <TableRow className={styles.loadingTableHead}>
              <TableCell className={styles.loadingHeaderCell}>
                <Typography className={styles.loadingHeaderText}>
                  Name
                </Typography>
              </TableCell>
              <TableCell className={styles.loadingHeaderCell}>
                <Typography className={styles.loadingHeaderText}>
                  Plan type
                </Typography>
              </TableCell>
              <TableCell className={styles.loadingHeaderCell}>
                <Typography className={styles.loadingHeaderText}>
                  Start
                </Typography>
              </TableCell>
              <TableCell className={styles.loadingHeaderCell}>
                <Typography className={styles.loadingHeaderText}>
                  End
                </Typography>
              </TableCell>
              <TableCell className={styles.loadingHeaderCell}>
                <Typography className={styles.loadingHeaderText}>
                  Active users / seats
                </Typography>
              </TableCell>
              <TableCell className={styles.loadingHeaderCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3].map((index) => (
              <TableRow key={index}>
                <TableCell className={styles.loadingDataCell}>
                  <Skeleton />
                </TableCell>
                <TableCell className={styles.loadingDataCell}>
                  <Skeleton />
                </TableCell>
                <TableCell className={styles.loadingDataCell}>
                  <Skeleton />
                </TableCell>
                <TableCell className={styles.loadingDataCell}>
                  <Skeleton />
                </TableCell>
                <TableCell className={styles.loadingDataCell}>
                  <Skeleton />
                </TableCell>
                <TableCell className={styles.loadingDataCell}>
                  <Skeleton />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    )
  }

  const organizationsCount = allPlans?.enterprisePlans?.count || 0
  const plans = allPlans?.enterprisePlans?.items || []

  return (
    <Paper elevation={0} className={styles.container}>
      <Box className={styles.header}>
        <Typography className={styles.headerTitle}>Organizations</Typography>
        <Typography className={styles.headerCount}>
          {organizationsCount}
        </Typography>
      </Box>

      <Table className={styles.table}>
        <TableHead className={styles.tableHead}>
          <TableRow>
            <TableCell className={styles.headerCellName}>
              <Typography className={styles.headerText}>Name</Typography>
            </TableCell>
            <TableCell className={styles.headerCellPlanType}>
              <Typography className={styles.headerText}>Plan type</Typography>
            </TableCell>
            <TableCell className={styles.headerCellDate}>
              <Typography className={styles.headerText}>Start</Typography>
            </TableCell>
            <TableCell className={styles.headerCellDate}>
              <Typography className={styles.headerText}>End</Typography>
            </TableCell>
            <TableCell className={styles.headerCellSeats}>
              <Typography className={styles.headerText}>
                Active users / seats
              </Typography>
            </TableCell>
            <TableCell className={styles.headerCell} />
          </TableRow>
        </TableHead>
        <TableBody>
          {plans.map((plan, index) => (
            <React.Fragment key={plan.id}>
              <EnterprisePlansRow
                plan={plan}
                isExpanded={expandedRows.has(plan.id)}
                onToggle={() => toggleRow(plan.id)}
                rowIndex={index}
              />
              <EnterprisePlansDetail
                plan={plan}
                isExpanded={expandedRows.has(plan.id)}
              />
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}
